import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import config from "../../config";
import { Row, Col, Button, Skeleton, DatePicker, Form, Alert } from "antd";
import { NavLink } from "react-router-dom";
import { PrinterOutlined } from "@ant-design/icons";
import LoginChecked from "../Shared/LoginChecked";
import { NumberFormat } from "../Shared/Utility";
import { parseReportAmount } from "../../Pdf/PrintFormateNumber";
import { IdentificationIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import moment from "moment";

const ASSET_ITEMS = [
  { label: "Stock value (at cost)", key: "productBalance" },
  { label: "Customer receivable", key: "customerReceiveable" },
  { label: "Supplier receivable", key: "supplierReceiveable" },
  { label: "Bank balance", key: "bankBalance" },
  { label: "Cash in hand", key: "cashBalance" },
];

const LIABILITY_ITEMS = [
  { label: "Customer payable", key: "customerPayable" },
  { label: "Supplier payable", key: "supplierPayable" },
  { label: "Capital", key: "capitalBalance" },
];

function FinancialSummary() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const [loading, setLoading] = useState(false);
  const [finalData, setFinalData] = useState({});
  const [asOfDate, setAsOfDate] = useState(dayjs());

  useEffect(() => {
    document.title = "Financial Summary";
    setLoading(true);

    axios({
      method: "post",
      url: config.base_url + "Report/GetFinancialSummary",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        EndDate: dayjs(asOfDate).format("YYYY-MM-DD"),
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setFinalData(response.data);
        } else {
          setFinalData({});
        }
        setLoading(false);
      })
      .catch(function () {
        setFinalData({});
        setLoading(false);
      });
  }, [asOfDate, UserID, AccessKey]);

  const totals = useMemo(() => {
    const totalAssets =
      parseReportAmount(finalData.totalAssets) ||
      ASSET_ITEMS.reduce(
        (sum, item) => sum + parseReportAmount(finalData[item.key]),
        0
      );
    const totalLiabilities =
      parseReportAmount(finalData.totalLiabilities) ||
      LIABILITY_ITEMS.reduce(
        (sum, item) => sum + parseReportAmount(finalData[item.key]),
        0
      );
    const grossAmount =
      parseReportAmount(finalData.grossAmount) || totalAssets - totalLiabilities;
    return { totalAssets, totalLiabilities, grossAmount };
  }, [finalData]);

  const fmt = (val) => NumberFormat(parseReportAmount(val), 0);

  const asOfLabel = moment(asOfDate).format(config.date_format);
  const isProfit = totals.grossAmount >= 0;

  const disabledDate = (current) =>
    current && current > moment().endOf("day");

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h3 className="page-title">Financial Summary</h3>

            <div className="header-actions">
              <NavLink
                to={`/report-final/print-list?AsOfDate=${dayjs(asOfDate).format(
                  "YYYY-MM-DD"
                )}`}
                target="_blank"
              >
                <Button
                  type="dashed"
                  shape="round"
                  size="large"
                  icon={<PrinterOutlined />}
                >
                  Print Report
                </Button>
              </NavLink>
            </div>
          </div>

          <div className="filter-wrap">
            <Form layout="inline">
              <Form.Item label="As of date">
                <DatePicker
                  value={asOfDate}
                  onChange={(date) => date && setAsOfDate(date)}
                  disabledDate={disabledDate}
                  allowClear={false}
                />
              </Form.Item>
            </Form>
          </div>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Balances as of ${asOfLabel}`}
            description="This report is a point-in-time snapshot of stock and account balances, not a period activity report."
          />

          {loading ? (
            <Row>
              <Col xs={24} md={16}>
                <Skeleton active />
                <Skeleton active />
              </Col>
            </Row>
          ) : (
            <Row justify="center" gutter={[24, 24]}>
              <Col md={10} sm={24}>
                <div className="fb-card">
                  <div className="fb-card-header">
                    <div className="fb-card-title">
                      <span>
                        <IdentificationIcon />
                      </span>
                      <h4>Assets / Receivables</h4>
                    </div>
                  </div>
                  <div className="fb-card-body table">
                    <ul className="profit-list">
                      {ASSET_ITEMS.map((item) => (
                        <li key={item.key}>
                          <h4>{item.label}</h4>
                          <p>{fmt(finalData[item.key])}</p>
                        </li>
                      ))}
                      <li>
                        <h4 style={{ color: "#405289" }}>Total assets</h4>
                        <p className="netprofit">{fmt(totals.totalAssets)}</p>
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>

              <Col md={10} sm={24}>
                <div className="fb-card">
                  <div className="fb-card-header">
                    <div className="fb-card-title">
                      <span>
                        <IdentificationIcon />
                      </span>
                      <h4>Liabilities / Payables</h4>
                    </div>
                  </div>
                  <div className="fb-card-body table">
                    <ul className="profit-list">
                      {LIABILITY_ITEMS.map((item) => (
                        <li key={item.key}>
                          <h4>{item.label}</h4>
                          <p>{fmt(finalData[item.key])}</p>
                        </li>
                      ))}
                      <li>
                        <h4 style={{ color: "#405289" }}>Total liabilities</h4>
                        <p className="netprofit">{fmt(totals.totalLiabilities)}</p>
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>

              <Col md={20} sm={24}>
                <div className="fb-card">
                  <div className="fb-card-body table">
                    <ul className="profit-list">
                      <li>
                        <h4
                          style={{
                            color: isProfit ? "rgb(85 162 107)" : "#ec0d0d",
                          }}
                        >
                          Net position (assets − liabilities)
                        </h4>
                        <p className={isProfit ? "profit" : "loss"}>
                          {fmt(totals.grossAmount)}
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </>
  );
}

export default FinancialSummary;
